import { useEffect } from "react";
import { FetcherWithComponents, useFetcher } from "react-router-dom";
import { useBillingNavigation } from "v2/pages/billing/hooks/useBillingNavigation";
import Modal from "components/Modal";
import Button from "components/Button";
import WarningBox from "components/WarningBox";
import { RiCheckboxCircleFill } from "@remixicon/react";
import scraperApi from "api";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";
import { trackBillingV2ModalOpened } from "utils/Tracking";
import SubmitButton from "components/SubmitButton";


interface DeleteAccountConfirmContentProps {
  onClose: () => void;
  fetcher: FetcherWithComponents<any>;
}

const DELETE_WARNINGS = [
  "All your data will be permanently deleted",
  "Your API keys will be immediately revoked",
  "All active subscriptions will be cancelled",
  "You will not be able to recover your account",
];

function DeleteAccountConfirmContent({
  onClose,
  fetcher
}: DeleteAccountConfirmContentProps) {
  const isIdle = fetcher.state === "idle";

  return (
    <div className="flex flex-col">
      {/* Header with close button */}
      <div className="flex items-center justify-between pt-5 pb-2 px-6">
        <h2 className="text-base font-medium text-gray-900">
          Delete my account
        </h2>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-6 px-6 pt-3 pb-6">
        <p className="text-base leading-6 text-neutral-700">
          Permanently delete your account and all associated data.{" "}
          This action cannot be undone.
        </p>

        <WarningBox
          variant="danger"
          title="Warning: This action is permanent and cannot be reversed"
          items={DELETE_WARNINGS.map((w) => ({ text: w }))}
        />

        {/* Action buttons */}
        <fetcher.Form
          method="POST"
          action="/user-data/delete-account"
          className="flex gap-2"
        >
          <SubmitButton
            text={ isIdle ? "Delete my account permanently" : "Deleting..." }
            className="button button-primary destructive flex-1"
            size="SM"
            fetchersToCheck={ [ fetcher ] }
            disabled={ !isIdle }
          />
          <Button
            text="Keep my account"
            onClick={ onClose }
            className="button button-secondary flex-1"
            size="SM"
            disabled={ !isIdle }
          />
        </fetcher.Form>
      </div>
    </div>
  );
}
function AccountDeletedContent() {
  return (
    <div className="flex flex-col">
      {/* Content */}
      <div className="flex flex-col gap-6 items-center px-6 pb-6">
        {/* Pulsing green tick */}
        <div className="bg-success-50 rounded-full p-1 animate-pulse">
          <RiCheckboxCircleFill className="size-16 text-green-500" />
        </div>

        <h2 className="text-2xl font-semibold text-neutral-700">
          Account Deleted
        </h2>

        <div className="bg-success-50 border border-success-100 rounded p-4 w-full">
          <p className="text-base leading-6 text-neutral-700 text-center">
            Your request to delete your account has been successful.{" "}
            In a few seconds you will be logged out.
          </p>
        </div>
      </div>

      {/* Progress bar - fills over 6 seconds before redirect */}
      <div className="relative h-2 w-full bg-neutral-100 overflow-hidden rounded-b">
        <div
          className="absolute h-full bg-primary-600 transition-all ease-linear"
          style={{
            width: "100%",
            transitionDuration: "6s",
          }}
          ref={(el) => {
            // Start from 0 width, then animate to 100%
            if (el) {
              el.style.width = "0%";
              requestAnimationFrame(() => {
                el.style.width = "100%";
              });
            }
          }}
        />
      </div>
    </div>
  );
}

interface RequestReceivedContentProps {
  onClose: () => void;
}

function RequestReceivedContent({ onClose }: RequestReceivedContentProps) {
  return (
    <div className="flex flex-col gap-6 p-6">
      <h2 className="text-2xl font-semibold text-center text-gray-900">
        We have received your request
      </h2>

      <p className="text-sm text-center text-gray-600">
        Our team will process your request and contact you when complete.
      </p>

      <WarningBox
        variant="danger"
        title="Warning: This action is permanent and cannot be reversed"
        items={DELETE_WARNINGS.map((w) => ({ text: w }))}
      />

      <Button
        text="CLOSE"
        onClick={onClose}
        className="button button-primary w-full"
        size="SM"
      />
    </div>
  );
}

export default function DeleteAccountModal() {
  const fetcher = useFetcher<{ response: string }>();
  useToasterFromActionData(fetcher);

  const { navigate, goBack } = useBillingNavigation();
  const actionData = fetcher.data;

  useEffect(() => { trackBillingV2ModalOpened('delete_account'); }, []);

  // 6-second logout countdown only fires when the backend physically deleted the
  // account (free users with no Chargebee history). Anything else is queued for CS
  // and surfaced via the request-received panel — modal stays open.
  useEffect(() => {
    if (actionData?.response === "deleted_permanently") {
      setTimeout(async () => {
        await scraperApi.auth.logout();
        navigate("/login");
      }, 6000);
    }
  }, [actionData, navigate]);

  const showSuccess = actionData?.response === "deleted_permanently";
  const showRequestReceived = actionData?.response === "deletion_requested";

  return (
    <Modal
      className={showSuccess || showRequestReceived ? "!max-w-[500px]" : "!max-w-[550px]"}
      onClose={ goBack }
    >
      {showSuccess ? (
        <AccountDeletedContent />
      ) : showRequestReceived ? (
        <RequestReceivedContent onClose={goBack} />
      ) : (
        <DeleteAccountConfirmContent
          onClose={ goBack }
          fetcher={ fetcher }
        />
      )}
    </Modal>
  );
}
